import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { getPendingPoas } from "@/features/eservices/api";
import type { PoaPendingRow } from "@/features/eservices/types";
import { formatShortDate } from "@/lib/format";

interface ClientGroup {
  client: string;
  clientName: string;
  count: number;
  latest: string;
}

/** Control Center "Transcripts" tab — pending POA/CAF authorizations awaiting
 * IRS approval, grouped per client (mirrors the legacy transcript-alerts feed).
 * Renders bare (no Card) for embedding in the tab body. */
export function DashboardTranscriptAlerts() {
  const [rows, setRows] = useState<PoaPendingRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getPendingPoas()
      .then((result) => active && setRows(result))
      .catch(() => active && toast.error("Couldn't load transcript alerts."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const groups = useMemo(() => {
    const byClient = new Map<string, ClientGroup>();
    for (const row of rows) {
      const existing = byClient.get(row.client);
      if (existing) {
        existing.count += 1;
        if (row.updatedAt > existing.latest) existing.latest = row.updatedAt;
      } else {
        byClient.set(row.client, {
          client: row.client,
          clientName: row.clientName,
          count: 1,
          latest: row.updatedAt,
        });
      }
    }
    return [...byClient.values()].sort((a, b) => b.latest.localeCompare(a.latest));
  }, [rows]);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        No pending transcript authorizations.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="[&>th]:text-muted-foreground [&>th]:text-xs [&>th]:font-semibold [&>th]:tracking-wide [&>th]:uppercase">
          <TableHead>Client</TableHead>
          <TableHead>Latest</TableHead>
          <TableHead className="text-right">Pending POAs</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {groups.map((g) => (
          <TableRow key={g.client} className="hover:bg-accent">
            <TableCell className="font-medium">
              <Link
                to={`/client/${g.client}/overview`}
                className="hover:text-primary hover:underline"
              >
                {g.clientName}
              </Link>
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
              {formatShortDate(g.latest)}
            </TableCell>
            <TableCell className="text-right font-semibold">{g.count}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
